import AddressBookPanel from 'Components/AddressBookPanel';
import Badge from 'Components/shared/Badge';
import { SecondaryMenu } from 'Components/shared/SecondaryMenu';
import { NYLAS_V3 } from 'Constants/featureFlags';
import {
  ADDRESS_BOOK_SUBMENU_OPTIONS,
  ADDRESS_BOOK_SUBPATHS,
} from 'Constants/routing';
import {
  STORE_NOTIFICATION,
  STORE_PERSON,
  STORE_PREFERENCE,
  STORE_UI,
} from 'Constants/stores';
import withRouter from 'Hocs/WithRouter';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { inject, observer } from 'mobx-react';
import { useUIStore } from 'Modules/ui/index.store';
import { useIsSecondaryMenuVisible } from 'Modules/ui/secondaryMenu/hooks/useIsSecondaryMenuVisible';
import React, { useCallback, useMemo } from 'react';
import { Grid } from 'semantic-ui-react';
import { hasStaleSources } from 'Utils/contactSyncSource';
import { IAddressBookContainerProps } from './interfaces';

const AddressBookContainer: React.FC<IAddressBookContainerProps> = ({
  ui,
  person,
  navigate,
}) => {
  const testid = 'addressBookContainer';

  const handleCloseDeletePopup = useCallback(() => {
    ui.setDeletePopupDisplay(false);
  }, [ui]);

  const handleCloseModals = useCallback(() => {
    handleCloseDeletePopup();
  }, [handleCloseDeletePopup]);

  const handleHeaderClick = useCallback(() => {
    const { pathname } = window.location;

    person.setIsAddingContact(true);
    person.setEditContact(null);

    const isViewingExistingContact =
      pathname.includes('contact') &&
      !pathname.includes('contacts') &&
      person.editContactDetails &&
      !person.isAddingContact;

    const navigationState = isViewingExistingContact
      ? { state: { from: '/contacts' } }
      : undefined;

    void navigate('/addressBook/contact', navigationState);

    person.setIsAddingContact(false);
    useUIStore.getState().resetEntitySidePanel();
  }, [navigate, person]);

  const { [NYLAS_V3]: isNylasV3FlagEnabled } = useFeatureFlags([NYLAS_V3]);

  const optionsWithBadges = useMemo(() => {
    const hasV2Sources = person.allSources?.some(
      (source) =>
        !['google', 'microsoft', 'icloud'].includes(source.sourceData.type)
    );
    const isBadgeVisible =
      isNylasV3FlagEnabled &&
      (hasV2Sources || hasStaleSources(person.allSources));

    if (!isBadgeVisible) return ADDRESS_BOOK_SUBMENU_OPTIONS;

    return ADDRESS_BOOK_SUBMENU_OPTIONS.map((option) =>
      option.to === ADDRESS_BOOK_SUBPATHS.SOURCES
        ? { ...option, badge: <Badge level="warning" /> }
        : option
    );
  }, [person.allSources, isNylasV3FlagEnabled]);

  const isSecondaryMenuVisible = useIsSecondaryMenuVisible();

  return (
    <Grid.Column
      className="settings-container semantic-flex-row"
      onClick={handleCloseModals}
    >
      {isSecondaryMenuVisible && (
        <SecondaryMenu
          text="+ Add new contact"
          options={optionsWithBadges}
          header={{
            onClick: handleHeaderClick,
            active:
              window.location.pathname.includes('contact') &&
              !window.location.pathname.includes('contacts'),
          }}
        />
      )}
      <AddressBookPanel {...{ testid }} />
    </Grid.Column>
  );
};

export default inject(
  STORE_UI,
  STORE_PREFERENCE,
  STORE_NOTIFICATION,
  STORE_PERSON
)(withRouter(observer(AddressBookContainer)));
